'use client';

import { Button } from '@savvy-kit/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChevronDown } from 'lucide-react';

// launchdarkly-react-client-sdk camelCases flag keys, so the dashboard key
// `flag-eci-onboarding-send-to-ops` is read as `flagEciOnboardingSendToOps`.
const SEND_TO_OPS_FLAG = 'flagEciOnboardingSendToOps';

type OnboardingSendReminderMenuProps = {
  readonly householdName: string;
  readonly onResendToClient: () => void;
  readonly onSendToOps: () => void;
  readonly disabled?: boolean;
  readonly resendToClientDisabled?: boolean;
  /** Disable only the "Send to Savvy Ops" item (e.g. the household has no public id yet). */
  readonly sendToOpsDisabled?: boolean;
};

export function OnboardingSendReminderMenu({
  householdName,
  onResendToClient,
  onSendToOps,
  disabled = false,
  resendToClientDisabled = false,
  sendToOpsDisabled = false,
}: OnboardingSendReminderMenuProps) {
  const flags = useFlags();

  if (flags[SEND_TO_OPS_FLAG] !== true) {
    return (
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled || resendToClientDisabled}
        aria-label={`Resend invite for ${householdName}`}
        onClick={onResendToClient}
      >
        Resend invite
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button size="sm" variant="secondary" disabled={disabled} aria-label={`Send reminder for ${householdName}`} />
        }
      >
        Send reminder
        <ChevronDown className="size-3.5" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onResendToClient} disabled={resendToClientDisabled}>
          Resend to Client
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSendToOps} disabled={sendToOpsDisabled}>
          Send to Savvy Ops
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
